import type { ReactNode } from 'react';

import { cn } from '../../lib/utils';
import { DescriptionText } from './Typography';
import { HorizontalStack, VerticalStack } from './Stack';

export interface RegistryWorkspaceProps {
  children: ReactNode;
  utilityBand?: ReactNode;
  className?: string;
}

/**
 * Viewport-bound registry layout.
 *
 * The workspace owns vertical overflow so registry children can reserve stable
 * toolbar and pagination regions while their row viewport scrolls independently.
 */
export function RegistryWorkspace({
  children,
  utilityBand,
  className,
}: RegistryWorkspaceProps) {
  return (
    <section
      data-registry-workspace
      className={cn('flex min-h-0 flex-1 flex-col overflow-hidden', className)}
    >
      {utilityBand ? (
        <div data-registry-utility-band className="shrink-0">
          {utilityBand}
        </div>
      ) : null}
      <div
        data-registry-workspace-content
        className="flex min-h-0 flex-1 flex-col overflow-hidden"
      >
        {children}
      </div>
    </section>
  );
}

export interface RegistryTableSurfaceProps {
  /** Omit when the workspace header already supplies the dataset context. */
  title?: string;
  description?: string;
  count?: number;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}

/** A framed registry surface whose content remains bounded by its workspace. */
export function RegistryTableSurface({
  title,
  description,
  count,
  actions,
  children,
  className,
}: RegistryTableSurfaceProps) {
  return (
    <section
      data-registry-table-surface
      className={cn(
        'flex min-h-0 flex-1 flex-col overflow-hidden border border-border/60 bg-card/30 shadow-sm',
        className,
      )}
    >
      {title ? <header className="shrink-0 border-b border-border/40 bg-muted/5 px-5 py-4">
        <HorizontalStack className="items-start justify-between">
          <VerticalStack gap={2}>
            <HorizontalStack gap={2} className="items-center">
              <h2 className="text-[10px] font-black uppercase tracking-[0.24em] text-muted-foreground/60">
                {title}
              </h2>
              {count !== undefined ? (
                <span className="rounded border border-border/40 bg-muted/60 px-1.5 py-0.5 text-[9px] font-black text-muted-foreground/80">
                  {count}
                </span>
              ) : null}
            </HorizontalStack>
            {description ? (
              <DescriptionText className="max-w-2xl text-[11px] leading-relaxed">
                {description}
              </DescriptionText>
            ) : null}
          </VerticalStack>
          {actions}
        </HorizontalStack>
      </header> : null}
      <div className="flex min-h-0 flex-1 flex-col">{children}</div>
    </section>
  );
}
